import { DatePipe, NgClass } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { AuthService } from '../../auth/auth.service';
import { FilterConfig, GlobalFilterPanelComponent } from '../components/global-filter-panel/global-filter-panel.component';
import { DashboardService } from '../dashboard.service';
import { DashboardEngineService } from '../services/dashboard-engine.service';
import { ActivityTimelineComponent } from '../widgets/shared/activity-timeline/activity-timeline.component';
import { KpiCard, KpiCardGridComponent } from '../widgets/shared/kpi-card-grid/kpi-card-grid.component';
import { SubscriptionBillingCardComponent } from '../widgets/shared/subscription-billing-card/subscription-billing-card.component';

@Component({
  selector: 'app-sysadmin-dashboard',
  standalone: true,
  imports: [
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    MatProgressBarModule,
    MatProgressSpinnerModule,
    NgClass,
    DatePipe,
    GlobalFilterPanelComponent,
    KpiCardGridComponent,
    SubscriptionBillingCardComponent,
    ActivityTimelineComponent,
  ],
  templateUrl: './sysadmin-dashboard.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { style: 'display: block; width: 100%;' },
})
export class SysadminDashboardComponent {
  private engine = inject(DashboardEngineService);
  protected dashboardSvc = inject(DashboardService);
  private cdr = inject(ChangeDetectorRef);
  private destroyRef = inject(DestroyRef);
  private auth = inject(AuthService);

  readonly dashboardType = 'sysadmin';
  filters: any = {};

  get hasUsersPermission(): boolean {
    return this.auth.canAccess(['users_view']);
  }

  tenantData: any = null;
  userActivityData: any = null;
  healthData: any = null;
  securityData: any = null;

  loadingTenant = false;
  loadingUserActivity = false;
  loadingHealth = false;
  loadingSecurity = false;

  tenantKpis: KpiCard[] = [];

  readonly filterConfig: FilterConfig = {
    community: false,
    period: true,
    dateRange: false,
    status: false,
    priority: false,
  };

  onFiltersChanged(f: any): void {
    this.filters = { ...f };
    this.dashboardSvc.activeFilters.set(this.filters);
    this.loadAll();
    this.cdr.markForCheck();
  }

  private loadAll(): void {
    this.loadTenantOverview();
    this.loadUserActivity();
    this.loadSystemHealth();
    this.loadSecurityMonitoring();
  }

  private loadTenantOverview(): void {
    if (!this.hasUsersPermission) return;
    this.loadingTenant = true;
    this.engine
      .getWidgetData(this.dashboardType, 'tenantOverview', this.filters)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: any) => {
          const d = res.record?.data?.data ?? res.record?.data ?? res.record ?? {};
          this.tenantData = d;
          this.tenantKpis = [
            { label: 'Active Communities', value: d.communities ?? 0, icon: 'apartment', color: 'text-blue-600', bgColor: 'bg-blue-50' },
            { label: 'Active Units', value: d.units ?? 0, icon: 'home', color: 'text-indigo-600', bgColor: 'bg-indigo-50' },
            { label: 'Active Owners', value: d.owners ?? 0, icon: 'people', color: 'text-green-600', bgColor: 'bg-green-50' },
            { label: 'Active Residents', value: d.residents ?? 0, icon: 'person', color: 'text-teal-600', bgColor: 'bg-teal-50' },
            { label: 'Active Vendors', value: d.vendors ?? 0, icon: 'store', color: 'text-purple-600', bgColor: 'bg-purple-50' },
            { label: 'Active Users', value: d.active_users ?? 0, icon: 'manage_accounts', color: 'text-orange-600', bgColor: 'bg-orange-50' },
          ];
          this.loadingTenant = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.loadingTenant = false;
          this.cdr.markForCheck();
        },
      });
  }

  private loadUserActivity(): void {
    if (!this.hasUsersPermission) return;
    this.loadingUserActivity = true;
    this.engine
      .getWidgetData(this.dashboardType, 'userActivity', this.filters)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: any) => {
          this.userActivityData = res.record?.data?.data ?? res.record?.data ?? res.record ?? {};
          this.loadingUserActivity = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.loadingUserActivity = false;
          this.cdr.markForCheck();
        },
      });
  }

  private loadSystemHealth(): void {
    if (!this.hasUsersPermission) return;
    this.loadingHealth = true;
    this.engine
      .getWidgetData(this.dashboardType, 'systemHealth', this.filters)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: any) => {
          this.healthData = res.record?.data?.data ?? res.record?.data ?? res.record ?? {};
          this.loadingHealth = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.loadingHealth = false;
          this.cdr.markForCheck();
        },
      });
  }

  private loadSecurityMonitoring(): void {
    if (!this.hasUsersPermission) return;
    this.loadingSecurity = true;
    this.engine
      .getWidgetData(this.dashboardType, 'securityMonitoring', this.filters)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: any) => {
          this.securityData = res.record?.data?.data ?? res.record?.data ?? res.record ?? {};
          this.loadingSecurity = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.loadingSecurity = false;
          this.cdr.markForCheck();
        },
      });
  }

  getSubscriptionStatusColor(status: string): string {
    const map: any = { active: 'text-green-700 bg-green-50', expired: 'text-red-700 bg-red-50', trial: 'text-yellow-700 bg-yellow-50' };
    return map[status?.toLowerCase()] ?? 'text-gray-600 bg-gray-50';
  }
}
